import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowLeft, Sparkles } from 'lucide-react';

const CATEGORIES = [
  { value: 'saas', label: 'SaaS' },
  { value: 'ai_ml', label: 'AI / ML' },
  { value: 'sports_tech', label: 'Sports Tech' },
  { value: 'ecommerce', label: 'E-commerce / DTC' },
  { value: 'content', label: 'Content / Newsletter / Podcast' },
  { value: 'services', label: 'Services / Consulting' },
  { value: 'logistics', label: 'Logistics / Supply Chain' },
  { value: 'testing_qa', label: 'Testing / QA' },
];

export default function CostEstimatorPage() {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('saas');
  const [complexity, setComplexity] = useState('medium');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runEstimate = async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/v2/portfolio/estimate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name || 'New Business', category, complexity }),
      });
      const d = await r.json();
      setResult(d);
    } catch {
      setResult({ error: 'Failed to compute estimate.' });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/portfolio" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to Portfolio
          </Link>
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#3EF1C7]" />
            <span className="font-semibold">T-Shirt Sizing</span>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-3">Estimate your idea.</h1>
        <p className="text-lg text-[#94A3B8] mb-8">
          Tell us about your idea. We'll size it XS/S/M/L/XL across infrastructure, AI/ML,
          data sources, marketing, and domain. No spreadsheets. No vendor calls.
        </p>

        <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">Business name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g., Cricket Refreshed"
                className="w-full bg-[#0A0F1C] border border-[#22304A] rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full bg-[#0A0F1C] border border-[#22304A] rounded-lg px-3 py-2 text-white"
              >
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#94A3B8] mb-2">Complexity</label>
              <select
                value={complexity}
                onChange={e => setComplexity(e.target.value)}
                className="w-full bg-[#0A0F1C] border border-[#22304A] rounded-lg px-3 py-2 text-white"
              >
                <option value="low">Low — MVP / prototype</option>
                <option value="medium">Medium — launch-ready</option>
                <option value="high">High — scale-ready</option>
              </select>
            </div>
          </div>
          <button
            onClick={runEstimate}
            disabled={loading}
            className="px-6 py-3 bg-[#3EF1C7] text-[#0B1120] rounded-lg font-semibold flex items-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            {loading ? 'Computing...' : 'Estimate now'}
          </button>
        </div>

        {result && !result.error && (
          <div className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6">
            <h2 className="text-2xl font-bold mb-4">{name || 'New Business'} — Estimate</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              {['infrastructure', 'ai_ml_costs', 'data_sources', 'marketing', 'domain_hosting'].map(key => {
                const dim = result.estimate[key];
                if (!dim) return null;
                return (
                  <div key={key} className="bg-[#0A0F1C] border border-[#22304A] rounded-lg p-4">
                    <div className="text-xs text-[#64748B] uppercase mb-1">{key.replace(/_/g, ' ')}</div>
                    <div className="text-2xl font-bold text-[#3EF1C7]">{dim.size}</div>
                    <div className="text-xs text-[#94A3B8] mt-1">
                      ${dim.low_usd}–${dim.high_usd}/mo
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-[#0A0F1C] border border-[#22304A] rounded-lg p-4 mb-4">
              <div className="text-xs text-[#64748B] uppercase mb-1">Total monthly burn</div>
              <div className="text-3xl font-bold text-[#FFD93D]">
                ${result.estimate.total_monthly_low_usd}–${result.estimate.total_monthly_high_usd}/mo
              </div>
              <div className="text-sm text-[#94A3B8] mt-2">
                Time to MVP: <span className="text-white">{result.estimate.time_to_mvp_weeks} weeks</span>
              </div>
              <div className="text-sm text-[#94A3B8] mt-1">
                Revenue model: <span className="text-white">{result.estimate.revenue_model_suggestion}</span>
              </div>
            </div>

            <div className="bg-[#0A0F1C] border border-[#22304A] rounded-lg p-4">
              <div className="text-xs text-[#64748B] uppercase mb-2">WhatsApp / iMessage format</div>
              <pre className="whitespace-pre-wrap text-sm text-[#3EF1C7] font-mono">{result.message_format}</pre>
            </div>
          </div>
        )}

        {result && result.error && (
          <div className="bg-[#FF6B6B]/20 border border-[#FF6B6B] rounded-xl p-4 text-[#FF6B6B]">
            {result.error}
          </div>
        )}
      </div>
    </div>
  );
}
